import React, { Children } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/ui/Footer';
import { Award, Users, Target, Lightbulb } from 'lucide-react';
import { motion } from 'framer-motion';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.8,
    ease: [0.22, 1, 0.36, 1]
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
export function AboutUsPage() {
  return <div className="bg-black min-h-screen" data-id="element-210">
      <Header data-id="element-211" />
      <main className="pt-32 pb-24 px-6" data-id="element-212">
        <div className="max-w-7xl mx-auto" data-id="element-213">
          <motion.div className="mb-16" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-214">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6 font-roboto-condensed" data-id="element-215">
              About Us
            </h1>
            <p className="text-xl text-white/80 max-w-3xl leading-relaxed" data-id="element-216">
              We are enterprise data strategists who deliver premium insights at
              scale, trusted by healthcare leaders who value speed, precision,
              and successful outcomes.
            </p>
          </motion.div>
          <motion.div className="grid lg:grid-cols-2 gap-12 mb-16" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-217">
            <motion.div variants={fadeInUp} data-id="element-218">
              <h2 className="text-3xl font-semibold text-white mb-6 font-roboto-condensed" data-id="element-219">
                Our Mission
              </h2>
              <p className="text-white/70 leading-relaxed mb-4" data-id="element-220">
                We exist to transform healthcare data chaos into strategic
                clarity. Our specialized focus on U.S. healthcare data
                management ensures that organizations can make confident,
                data-driven decisions that improve patient care and operational
                efficiency.
              </p>
              <p className="text-white/70 leading-relaxed" data-id="element-221">
                With deep expertise in Cloud Data Strategy, Master & Reference
                Data Management (MDM/RDM), Enterprise Data Governance, Data
                Cataloging, and Data Stewardship programs, we deliver measurable
                results that drive long-term success.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all duration-300" whileHover={{
            y: -8
          }} data-id="element-222">
              <h3 className="text-2xl font-semibold text-white mb-6 font-roboto-condensed" data-id="element-223">
                What Sets Us Apart
              </h3>
              <ul className="space-y-4" data-id="element-224">
                {[{
                icon: Award,
                title: 'Enterprise Scale Expertise',
                desc: 'Trusted by C-suite and senior leaders for high-stakes engagements'
              }, {
                icon: Target,
                title: 'Healthcare Specialization',
                desc: 'Deep understanding of U.S. healthcare data ecosystems'
              }, {
                icon: Users,
                title: 'Proven Track Record',
                desc: 'Clients return for multiple phases due to measurable success'
              }, {
                icon: Lightbulb,
                title: 'Platform Expertise',
                desc: 'Deep knowledge of leading MDM and governance platforms'
              }].map((item, index) => <motion.li key={index} className="flex items-start gap-3" initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.1 * index,
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-225">
                    <item.icon className={`w-6 h-6 ${index % 2 === 0 ? 'text-purple-400' : 'text-cyan-400'} flex-shrink-0 mt-1`} data-id="element-226" />
                    <div data-id="element-227">
                      <h4 className="text-white font-semibold mb-1" data-id="element-228">
                        {item.title}
                      </h4>
                      <p className="text-white/70 text-sm" data-id="element-229">{item.desc}</p>
                    </div>
                  </motion.li>)}
              </ul>
            </motion.div>
          </motion.div>
          <motion.div className="bg-white/5 border border-white/10 rounded-2xl p-8 lg:p-12 mb-16" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-230">
            <h2 className="text-3xl font-semibold text-white mb-8 text-center font-roboto-condensed" data-id="element-231">
              Platform Expertise
            </h2>
            <p className="text-white/70 text-center mb-8 max-w-3xl mx-auto" data-id="element-232">
              We bring deep subject matter expertise across leading MDM and
              governance platforms:
            </p>
            <motion.div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
            once: true,
            margin: '-100px'
          }} data-id="element-233">
              {[{
              name: 'Profisee',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/g3ozH3aPbyxxRenLG5cR3k/pasted-image.png'
            }, {
              name: 'Ataccama',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jVQLTLF3DWfWU79kGWRhXV/pasted-image.png'
            }, {
              name: 'Gaine',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sNHBiBuBjErXzsLzJqMBUV/gaine.png'
            }, {
              name: 'Verato',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6CGgdUAxGymJ9LosRcTi9c/verato.png'
            }, {
              name: 'Collibra',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/v7FTnYCY3HX4t4n8NoW28x/pasted-image.png'
            }, {
              name: 'Microsoft Purview',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aAoSHN6cvxdiQ3cjJ2ahu7/pasted-image.png'
            }, {
              name: 'LexisNexis',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1zPaarFZVXJ5b1SXdsBvVb/pasted-image.png'
            }, {
              name: 'Smarty',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/auYifGga1C9aaABizxs8v8/pasted-image.png'
            }, {
              name: 'Melissa',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hFXZ7GXZ9Wae5S7Cn59eQG/pasted-image.png'
            }, {
              name: 'ESRI',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1B7noyhdXaD5dUU4SQ5ij1/esri.png'
            }, {
              name: 'Dun & Bradstreet',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hXMgP54Ya5wKyJicjjum8C/pasted-image.svg'
            }, {
              name: 'NPPES',
              logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2LbHWopLawkQTBkaDHFeg8/pasted-image.png'
            }].map((platform, index) => <motion.div key={index} variants={fadeInUp} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-center justify-center hover:bg-white/10 transition-all duration-300 min-h-[100px]" whileHover={{
              scale: 1.08,
              y: -5
            }} data-id="element-234">
                  <img src={platform.logo} alt={platform.name} className="w-full h-auto object-contain max-h-12" data-id="element-235" />
                </motion.div>)}
            </motion.div>
          </motion.div>
          <motion.div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8 lg:p-12" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-236">
            <h2 className="text-3xl font-semibold text-white mb-8 text-center font-roboto-condensed" data-id="element-237">
              Client Testimonials
            </h2>
            <motion.div className="space-y-8 max-w-4xl mx-auto" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
            once: true,
            margin: '-100px'
          }} data-id="element-238">
              {[{
              quote: 'Brigitte is an exceptional advisor and an asset to any organization seeking to enhance its data management and governance capabilities. Her contributions have been a key factor in our success, and I wholeheartedly recommend her services.',
              author: 'Alex Rothberg',
              role: 'Senior Integration Manager at Laticrete',
              image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mB6fLqksgg5ijcouP469FT/pasted-image.jpg'
            }, {
              quote: 'Brigitte is a brilliant leader in the data governance space. Her biggest strength is her ability to translate the murky waters of data governance into clear markers that clarify priorities and articulate a clear roadmap for success.',
              author: 'Peter Nichol',
              role: 'Strategic Business Relationship Manager',
              image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8NSdQ4mLUnsvej8S3ShZmA/pasted-image.jpg'
            }].map((testimonial, index) => <motion.div key={index} variants={fadeInUp} className="bg-black/30 border border-white/10 rounded-xl p-6 hover:bg-black/40 transition-all duration-300" whileHover={{
              scale: 1.02,
              y: -5
            }} data-id="element-239">
                  <p className="text-white/80 italic mb-4 leading-relaxed" data-id="element-240">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-4" data-id="element-241">
                    <img src={testimonial.image} alt={testimonial.author} className="w-14 h-14 rounded-full object-cover" data-id="element-242" />
                    <div data-id="element-243">
                      <p className="text-white font-semibold" data-id="element-244">
                        {testimonial.author}
                      </p>
                      <p className="text-white/60 text-sm" data-id="element-245">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-246" />
    </div>;
}